/**
 * @typedef {Object} BuiltJournalForm
 * @property {string} journalType
 * @property {Object} formData
 * @property {Set<string>} displayedFields
 * @property {Set<string>} readonlyFields
 */

export function buildJournalFormData({
  journalType,
  visit,
  invoiceLine,
  chargeCaptureItem,
  matchedInvoiceLine,
  discountItem,
  currentUser,
}) {
  if (!visit) {
    throw new Error('All journal builds require visit context');
  }

  switch (journalType) {
    case 'invoice':
      return buildInvoiceJournal({ visit, invoiceLine, currentUser });

    case 'blank':
      return buildBlankJournal({ visit, currentUser });

    case 'medical_note':
      return buildMedicalNoteJournal({ visit, chargeCaptureItem, currentUser });

    case 'charge_capture':
      return buildChargeCaptureJournal({
        visit,
        chargeCaptureItem,
        matchedInvoiceLine,
        currentUser,
      });

    case 'discount':
      return buildDiscountJournal({ visit, discountItem, currentUser });

    default:
      throw new Error(`Unknown journalType: ${journalType}`);
  }
}

/* =========================================================
   SHARED BASE
   ========================================================= */

  function buildVisitBase(visit) {
  const rawUniqueId = visit.unique_id ?? '';

  const instinctVisitId = rawUniqueId.includes('_')
    ? rawUniqueId.split('_').pop()
    : '';

  return {
    bp_hospital: visit.revenue_id ?? '',
    instinct_visit_id: instinctVisitId,
    patient_id: visit.patient_pims_id ?? '',
    unique_id: rawUniqueId,
  };
} 

function buildJournalDefaults({ visit, currentUser }) {
  const now = new Date().toISOString();

  return {
    ...buildVisitBase(visit),

    completed_date: null,
    staff_id: '',
    invoiceitemid: '',
    charge_type: '',

    previous_charge: 0,
    expected_charge: 0,
    adjusted_charge: 0,

    label: '',
    reason: '',
    note: '',

    entry_date: now,
    email:
      currentUser?.mail ||
      currentUser?.email ||
      currentUser?.userPrincipalName ||
      '',

    includeChecked: false,
    isFinalized: false,
    load_date: now,
    finalize_date: null,

    is_escalated: false,
    is_pricing: false,
    is_no_error: false,
    is_placeholder: false,
    is_local_leadership: false,
  };
}

/* =========================================================
   INVOICE
   ========================================================= */
function buildInvoiceJournal({ visit, invoiceLine, currentUser }) {
  if (!invoiceLine) throw new Error('Invoice journal requires invoiceLine');

  return {
    journalType: 'invoice',
    formData: {
      ...buildJournalDefaults({ visit, currentUser }),

      completed_date: invoiceLine.completed_date ?? null,
      staff_id: invoiceLine.user ?? '',
      invoiceitemid: invoiceLine.pims_id ?? '',
      charge_type: invoiceLine.is_voided ? 'Void Charge' : 'Missed Charge',

      label: invoiceLine.label ?? '',
      previous_charge: 0,
      expected_charge: Number(invoiceLine.amount ?? 0),
      adjusted_charge: Number(invoiceLine.recovered_amount ?? 0),

      reason: invoiceLine.reason ?? '',
      note: invoiceLine.notes ?? '',
    },

    displayedFields: new Set([
      'invoiceitemid',
      'label',
      'expected_charge',
      'staff_id',
      'completed_date',
      'adjusted_charge',
      'reason',
      'note',
    ]),

    readonlyFields: new Set([
      'invoiceitemid',
      'label',
      'completed_date',
    ]),
  };
}

/* =========================================================
   BLANK
   ========================================================= */
function buildBlankJournal({ visit, currentUser }) {
  return {
    journalType: 'blank',
    formData: {
      ...buildJournalDefaults({ visit, currentUser }),
      charge_type: 'Blank Entry',
    },

    displayedFields: new Set([
      'invoiceitemid',
      'staff_id',
      'expected_charge',
      'adjusted_charge',
      'reason',
      'note',
    ]),

    readonlyFields: new Set([]),
  };
}

/* =========================================================
   MEDICAL NOTE
   ========================================================= */
function buildMedicalNoteJournal({ visit, chargeCaptureItem, currentUser }) {
  if (!chargeCaptureItem)
    throw new Error('Medical note journal requires chargeCaptureItem');

  return {
    journalType: 'medical_note',
    formData: {
      ...buildJournalDefaults({ visit, currentUser }),

      charge_type: 'Medical Note Logic',
      invoiceitemid: chargeCaptureItem.rule_id ?? '',
      label: chargeCaptureItem.title ?? '',

      expected_charge: Number(chargeCaptureItem.price ?? 0),
    },

    displayedFields: new Set([
      'label',
      'expected_charge',
      'invoiceitemid',
      'staff_id',
      'adjusted_charge',
      'reason',
      'note',
    ]),

    readonlyFields: new Set([
      'label',
      'invoiceitemid',
      'charge_type',
    ]),
  };
}

/* =========================================================
   CHARGE CAPTURE
   ========================================================= */
  function extractFirstPimsCode(codes) {
    if (typeof codes !== 'string') return '';
    return codes.split(',')[0]?.trim() ?? '';
  }

  function buildChargeCaptureJournal({
  visit,
  chargeCaptureItem,
  matchedInvoiceLine,
  currentUser,
}) {
  if (!chargeCaptureItem)
    throw new Error('Charge capture journal requires chargeCaptureItem');

  const matched = matchedInvoiceLine ?? {};
  const derivedPimsId = extractFirstPimsCode(chargeCaptureItem.codes_that_are_needed);

  return {
    journalType: 'charge_capture',
    formData: {
      ...buildJournalDefaults({ visit, currentUser }),

      completed_date: matched.completed_date ?? null,
      staff_id: matched.user ?? '',
      invoiceitemid: derivedPimsId ?? '',
      charge_type: 'Charge Capture Logic',

      label: chargeCaptureItem.title ?? '',
      previous_charge: Number(chargeCaptureItem.price ?? 0),
      expected_charge: Number(chargeCaptureItem.price ?? 0),
    },

    displayedFields: new Set([
      'label',
      'expected_charge',
      'previous_charge',
      'completed_date',
      'invoiceitemid',
      'staff_id',
      'adjusted_charge',
      'reason',
      'note',
    ]),

    readonlyFields: new Set([
      'label',
      'previous_charge',
      'completed_date',
    ]),
  };
}

/* =========================================================
   DISCOUNT
   ========================================================= */
function buildDiscountJournal({ visit, discountItem, currentUser }) {
  if (!discountItem)
    throw new Error('Discount journal requires discountItem');

  const parent = discountItem.parent ?? {};

  return {
    journalType: 'discount',
    formData: {
      ...buildJournalDefaults({ visit, currentUser }),

      completed_date: parent.datetime ?? null,
      staff_id: parent.staffid ?? '',
      invoiceitemid: parent.invoiceitemid ?? '',
      charge_type: 'Discount Logic',

      label: discountItem.title ?? 'Discount',
      previous_charge: Number(parent.gross_revenue ?? 0),
      expected_charge: Number(discountItem.discountAmount ?? 0),
    },

    displayedFields: new Set([
      'label',
      'expected_charge',
      'completed_date',
      'invoiceitemid',
      'staff_id',
      'adjusted_charge',
      'reason',
      'note',
    ]),

    readonlyFields: new Set([
      'label',
      'completed_date',
      'invoiceitemid',
    ]),
  };
}